import React, { useEffect, useState } from "react";
import {
  resolveMediaUrl,
  resolveMediaBackground,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import {
  checkCommunityHandle,
  saveCommunityHandle,
  saveCommunityProfile,
} from "./communitySocialApi";
import CommunityFeedPanel from "./CommunityFeedPanel";
import CommunityFollowButton from "./CommunityFollowButton";

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Community Profile — public social identity + timeline.
 * Reuses shared CommunityFeedPanel / ActivityFeed for posts (no duplicate renderer).
 * Follow button for other members reuses CommunityFollowButton (type=user).
 */
function CommunityProfilePanel({
  profile = null,
  onProfileUpdate = null,
  readOnly = false,
  timelineItems = null,
  timelineLoading = false,
  timelineError = false,
  timelineHasMore = false,
  timelineLoadingMore = false,
  onTimelineRetry = null,
  onTimelineLoadMore = null,
  onTimelineItemUpdate = null,
}) {
  const [handleDraft, setHandleDraft] = useState("");
  const [handleMessage, setHandleMessage] = useState("");
  const [savingHandle, setSavingHandle] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const isOwner = !readOnly && !!(profile && profile.is_owner);
  const showFollow =
    !!profile &&
    !!profile.id &&
    !profile.is_owner &&
    (readOnly || !isOwner);

  useEffect(() => {
    if (profile && profile.handle) {
      setHandleDraft(profile.handle);
    }
  }, [profile]);

  const coverPath =
    (profile && (profile.cover || profile.community_cover)) || null;
  const avatarPath = (profile && profile.avatar) || null;
  const displayName =
    (profile && profile.display_name) ||
    (!readOnly ? localStorage.getItem("username") : null) ||
    "Community member";

  const saveHandle = async (event) => {
    event.preventDefault();
    if (!isOwner) return;
    setSavingHandle(true);
    setHandleMessage("");
    setError("");
    const check = await checkCommunityHandle(handleDraft);
    if (!check.available) {
      setHandleMessage(check.reason || "Handle unavailable");
      setSavingHandle(false);
      return;
    }
    const result = await saveCommunityHandle(check.handle || handleDraft);
    setSavingHandle(false);
    if (result.error) {
      setHandleMessage(result.message || "Unable to save handle");
      return;
    }
    if (typeof onProfileUpdate === "function") onProfileUpdate(result.profile);
    setHandleMessage("Handle saved.");
    setStatus("Profile updated.");
  };

  const uploadAvatar = async (event) => {
    if (!isOwner) return;
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (IMAGE_TYPES.indexOf(file.type) < 0) {
      setError("Profile picture must be an image (JPG, PNG, GIF, or WebP).");
      return;
    }
    setBusy(true);
    setError("");
    const result = await saveCommunityProfile({ avatar: file });
    setBusy(false);
    if (result.error) {
      setError(result.message || "Unable to upload profile picture");
      return;
    }
    if (typeof onProfileUpdate === "function") onProfileUpdate(result.profile);
    setStatus("Profile picture updated.");
  };

  const uploadCover = async (event) => {
    if (!isOwner) return;
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (IMAGE_TYPES.indexOf(file.type) < 0) {
      setError("Cover image must be an image (JPG, PNG, GIF, or WebP).");
      return;
    }
    setBusy(true);
    setError("");
    const result = await saveCommunityProfile({ cover: file });
    setBusy(false);
    if (result.error) {
      setError(result.message || "Unable to upload cover image");
      return;
    }
    if (typeof onProfileUpdate === "function") onProfileUpdate(result.profile);
    setStatus("Cover image updated.");
  };

  const clearCover = async () => {
    if (!isOwner) return;
    setBusy(true);
    setError("");
    const result = await saveCommunityProfile({ clear_cover: true });
    setBusy(false);
    if (result.error) {
      setError(result.message || "Unable to remove cover");
      return;
    }
    if (typeof onProfileUpdate === "function") onProfileUpdate(result.profile);
    setStatus("Cover image removed.");
  };

  const coverStyle = coverPath
    ? { backgroundImage: resolveMediaBackground(coverPath) }
    : undefined;

  return (
    <section
      className="community-profile-panel"
      aria-labelledby="community-profile-heading"
    >
      <div className="community-profile-panel__head">
        <h2 id="community-profile-heading" className="community-social__feed-title">
          {isOwner ? "Community Profile" : displayName}
        </h2>
      </div>

      <div
        className={
          "community-profile-panel__cover" +
          (coverPath ? " community-profile-panel__cover--has-image" : "")
        }
        style={coverStyle}
        role={coverPath ? "img" : undefined}
        aria-label={coverPath ? `${displayName} cover` : undefined}
      >
        {!coverPath ? (
          <div className="community-profile-panel__cover-empty">
            Community cover image
          </div>
        ) : null}
        {isOwner ? (
          <div className="community-profile-panel__cover-actions">
            <label className="community-profile-panel__upload-btn">
              {coverPath ? "Change cover" : "Upload cover"}
              <input
                type="file"
                accept={IMAGE_TYPES.join(",")}
                className="sr-only"
                onChange={uploadCover}
                disabled={busy}
              />
            </label>
            {coverPath ? (
              <button
                type="button"
                className="community-profile-panel__text-btn"
                onClick={clearCover}
                disabled={busy}
              >
                Remove
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="community-profile-panel__identity">
        {isOwner ? (
          <label className="community-profile-panel__avatar">
            {avatarPath ? (
              <img
                src={resolveMediaUrl(avatarPath)}
                alt=""
                onError={handleMediaImgError}
              />
            ) : (
              <span>{(displayName || "?").charAt(0).toUpperCase()}</span>
            )}
            <input
              type="file"
              accept={IMAGE_TYPES.join(",")}
              className="sr-only"
              onChange={uploadAvatar}
              disabled={busy}
              aria-label="Upload community profile picture"
            />
            <span className="community-profile-panel__avatar-hint">Change</span>
          </label>
        ) : (
          <span className="community-profile-panel__avatar" aria-hidden="true">
            {avatarPath ? (
              <img
                src={resolveMediaUrl(avatarPath)}
                alt=""
                onError={handleMediaImgError}
              />
            ) : (
              <span>{(displayName || "?").charAt(0).toUpperCase()}</span>
            )}
          </span>
        )}
        <div className="community-profile-panel__identity-text">
          <p className="community-profile-panel__name">{displayName}</p>
          {profile && profile.handle ? (
            <p className="community-profile-panel__handle">@{profile.handle}</p>
          ) : isOwner ? (
            <p className="community-profile-panel__handle community-profile-panel__handle--warn">
              Choose a public handle
            </p>
          ) : null}
          {showFollow ? (
            <div className="community-profile-panel__follow">
              <CommunityFollowButton
                type="user"
                id={profile.id}
                initiallyFollowing={!!profile.is_following}
                followerCount={
                  typeof profile.follower_count === "number"
                    ? profile.follower_count
                    : null
                }
                showCount={typeof profile.follower_count === "number"}
                className="community-profile-panel__follow-btn"
              />
            </div>
          ) : null}
        </div>
      </div>

      {isOwner ? (
        <form className="community-profile-panel__form" onSubmit={saveHandle}>
          <label
            htmlFor="community-profile-handle"
            className="community-profile-panel__label"
          >
            Community Handle
          </label>
          <div className="community-create-post__handle-row">
            <span className="community-create-post__at" aria-hidden="true">
              @
            </span>
            <input
              id="community-profile-handle"
              className="community-create-post__handle-input"
              value={handleDraft}
              onChange={(e) => setHandleDraft(e.target.value.replace(/\s+/g, ""))}
              placeholder="yourname"
              autoComplete="off"
              maxLength={30}
              required
            />
            <button
              type="submit"
              className="btn btn-danger"
              disabled={savingHandle || !handleDraft.trim()}
            >
              {savingHandle ? "Saving…" : "Save handle"}
            </button>
          </div>
          {handleMessage ? (
            <p className="community-create-post__hint" role="status">
              {handleMessage}
            </p>
          ) : (
            <p className="community-create-post__hint">
              Letters, numbers, and underscores — 3 to 30 characters.
            </p>
          )}
        </form>
      ) : null}

      {error ? (
        <p className="community-create-post__error" role="alert">
          {error}
        </p>
      ) : null}
      {status && !error ? (
        <p className="community-create-post__hint" role="status">
          {status}
        </p>
      ) : null}

      <div className="community-profile-panel__timeline community-social__feed-wrap">
        <div className="community-social__feed-head">
          <h3 className="community-social__feed-title">Posts</h3>
          <p className="community-social__feed-note">Newest first</p>
        </div>
        <CommunityFeedPanel
          featureType="social"
          variant="posts"
          contextType="profile"
          title={null}
          loading={timelineLoading}
          error={timelineError}
          items={timelineItems || []}
          onRetry={onTimelineRetry}
          onItemUpdate={onTimelineItemUpdate}
          showRankingMeta={false}
          hasMore={timelineHasMore}
          loadingMore={timelineLoadingMore}
          onLoadMore={onTimelineLoadMore}
        />
      </div>
    </section>
  );
}

export default CommunityProfilePanel;
